import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Upload, Loader2, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { useTranslation } from "@/shared/i18n";
import type { CreatorBankAccount, CreatorReceivableSnapshot } from "../types";
import { getReceivableDisplayStatus } from "../types";
import { useCreateCreatorReceiptRequest } from "../model/hooks";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

const currency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);

const formatMonthShort = (paymentMonth: string) => {
  const [y, m] = paymentMonth.split("-");
  if (!y || !m) return paymentMonth;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return format(d, "MMM/yyyy", { locale: ptBR });
};

interface RequestReceiptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshots: CreatorReceivableSnapshot[];
  bankAccounts: CreatorBankAccount[];
  /** Mês a pré-selecionar na lista (ex.: linha da tabela que abriu o modal). */
  initialSelectedMonth?: string | null;
  onSuccess?: () => void;
  /** Chamado quando o usuário não tem conta e clica em cadastrar (para abrir o form no pai). */
  onOpenAddAccount?: () => void;
}

export function RequestReceiptModal({
  open,
  onOpenChange,
  snapshots,
  bankAccounts,
  initialSelectedMonth,
  onSuccess,
  onOpenAddAccount,
}: RequestReceiptModalProps) {
  const { t } = useTranslation("creator-financial");
  const [bankAccountId, setBankAccountId] = useState<string>("");
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set());
  const [file, setFile] = useState<File | null>(null);
  const { createReceiptRequest, isLoading: isSubmitting } =
    useCreateCreatorReceiptRequest();

  const eligibleSnapshots = useMemo(() => {
    return snapshots.filter(
      (s) => getReceivableDisplayStatus(s) === "awaiting_creator_action"
    );
  }, [snapshots]);

  useEffect(() => {
    if (open && initialSelectedMonth && eligibleSnapshots.some((s) => s.paymentMonth === initialSelectedMonth)) {
      setSelectedMonths(new Set([initialSelectedMonth]));
    }
    if (open && !initialSelectedMonth) {
      setSelectedMonths(new Set());
    }
  }, [open, initialSelectedMonth, eligibleSnapshots]);

  const defaultAccount = useMemo(
    () => bankAccounts.find((a) => a.default) ?? bankAccounts[0],
    [bankAccounts]
  );

  const totalSelected = useMemo(() => {
    return eligibleSnapshots
      .filter((s) => selectedMonths.has(s.paymentMonth))
      .reduce((acc, s) => acc + s.shareAmount, 0);
  }, [eligibleSnapshots, selectedMonths]);

  const toggleMonth = (paymentMonth: string) => {
    setSelectedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(paymentMonth)) next.delete(paymentMonth);
      else next.add(paymentMonth);
      return next;
    });
  };

  const handleSubmit = async () => {
    const accountId = bankAccountId || defaultAccount?.id;
    if (!accountId || selectedMonths.size === 0 || !file) return;
    try {
      await createReceiptRequest({
        payload: {
          bankAccountId: accountId,
          paymentMonths: Array.from(selectedMonths),
        },
        file,
      });
      onSuccess?.();
      onOpenChange(false);
      setSelectedMonths(new Set());
      setFile(null);
    } catch {
      // Toast de erro já exibido pelo hook
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelectedMonths(new Set());
      setFile(null);
    }
    onOpenChange(next);
  };

  const noAccounts = bankAccounts.length === 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-800 border-white/10 text-white rounded-2xl shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-white">
            {t("requestReceiptModal.title")}
          </DialogTitle>
        </DialogHeader>

        {noAccounts ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-zinc-400">
              {t("requestReceiptModal.noAccountMessage")}
            </p>
            <Button
              type="button"
              className="bg-[#FF2E6A] hover:bg-[#ff4370] text-white border-0"
              onClick={() => {
                onOpenChange(false);
                onOpenAddAccount?.();
              }}
            >
              {t("requestReceiptModal.noAccountCta")}
            </Button>
          </div>
        ) : (
          <div className="space-y-6 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">
                {t("requestReceiptModal.bankAccount")}
              </Label>
              <Select
                value={(bankAccountId || defaultAccount?.id) ?? ""}
                onValueChange={setBankAccountId}
              >
                <SelectTrigger className="border-white/10 bg-zinc-700/80 text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-[#FF2E6A] focus:ring-offset-0 focus:ring-offset-zinc-800">
                  <SelectValue placeholder={t("requestReceiptModal.bankAccountHint")} />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-zinc-800 text-white">
                  {bankAccounts
                    .filter((a) => a.active)
                    .map((acc) => (
                      <SelectItem
                        key={acc.id}
                        value={acc.id}
                        className="text-white focus:bg-zinc-700 focus:text-white"
                      >
                        {acc.bank} — {acc.account}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {eligibleSnapshots.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">
                    {t("requestReceiptModal.valuesToReceive")}
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-zinc-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-800 border-white/10 text-white">
                        <p className="max-w-xs">
                          {t("requestReceiptModal.valuesToReceiveHint")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <ul className="border border-white/10 rounded-md divide-y divide-white/10 max-h-40 overflow-y-auto bg-zinc-900/50">
                  {eligibleSnapshots.map((s) => (
                    <li
                      key={s.paymentMonth}
                      className={cn(
                        "flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5"
                      )}
                    >
                      <label className="flex items-center gap-2 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={selectedMonths.has(s.paymentMonth)}
                          onChange={() => toggleMonth(s.paymentMonth)}
                          className="rounded border-white/20 bg-zinc-700 accent-[#FF2E6A] focus:ring-2 focus:ring-[#FF2E6A] focus:ring-offset-0 focus:ring-offset-zinc-800"
                        />
                        <span className="text-white">
                          {formatMonthShort(s.paymentMonth)} {currency(s.shareAmount)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-zinc-400">
                  {t("requestReceiptModal.totalSelected")}
                </p>
                <p className="font-medium text-white">
                  {t("requestReceiptModal.total")}: {currency(totalSelected)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">
                {t("requestReceiptModal.uploadNfe")}
              </Label>
              <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center transition-colors hover:border-[#FF2E6A]/50 bg-zinc-900/50">
                <input
                  type="file"
                  accept=".pdf,.xml"
                  className="hidden"
                  id="nfe-upload"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <label
                  htmlFor="nfe-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="h-8 w-8 text-zinc-400" />
                  <span className="text-sm text-white">
                    {file ? file.name : t("requestReceiptModal.uploadNfePlaceholder")}
                  </span>
                </label>
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-white/10 p-3 bg-zinc-900/50">
              <p className="text-sm font-medium text-white">
                {t("requestReceiptModal.nfeExplanationTitle")}
              </p>
              <pre className="text-xs text-zinc-400 whitespace-pre-wrap font-sans">
                {t("requestReceiptModal.nfeExplanationBody")}
              </pre>
              <p className="text-xs text-zinc-400 mt-2">
                {t("requestReceiptModal.taxWithholdingNote")}
              </p>
            </div>

            <DialogFooter className="flex justify-end gap-2 pt-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10 hover:text-white"
                onClick={() => handleOpenChange(false)}
              >
                {t("requestReceiptModal.cancel")}
              </Button>
              <Button
                type="button"
                className="bg-[#FF2E6A] hover:bg-[#ff4370] text-white border-0 disabled:bg-zinc-600 disabled:text-zinc-400 disabled:opacity-90 disabled:hover:bg-zinc-600"
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  selectedMonths.size === 0 ||
                  !file ||
                  !(bankAccountId || defaultAccount?.id)
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("requestReceiptModal.submitting")}
                  </>
                ) : (
                  t("requestReceiptModal.submit")
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
